import { Fragment, useRef, useState } from "react"
import {
	Alert,
	Box,
	Button,
	Dropdown,
	Icons,
	Inline,
	Stack,
	Table,
	Text,
	Component,
} from "@sembark-travel/ui-base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui-dialog"
import { ListView, Search, useSearch } from "@sembark-travel/ui-list"
import { useXHR } from "@sembark-travel/xhr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import useSWR from "swr"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { IHotel } from "../Hotels"
import { Thumbnail } from "../shared/Thumbnail"
import {
	HotelConfigActionRequiredAlert,
	UnlockHotelConfigInDialog,
} from "../shared/HotelConfigLock"
import { AmenitiesList } from "../Amenities"
import { CodeChip, OtaMealCategoryBadge } from "../HotelOtaMealPlans"
import { hotelOtaRoomTypesXHR, TOtaRoomTypeWithMedia } from "./store"
import { OtaRoomTypeInDialog } from "./NewItem"
import {
	otaRoomTypeFormValuesToFormData,
	otaRoomTypeToFormValues,
} from "./utils"

export function HotelOtaRoomTypes({
	hotel,
	onChange,
}: {
	hotel: IHotel
	onChange: () => void
}) {
	const [params, setParams] = useSearch<Record<string, unknown>>({})
	const { hasPermission } = useCheckPermissions()
	const canManage = hasPermission(PERMISSIONS.CREATE_HOTELS)
	const xhr = useXHR()
	const [isAddOpen, openAddDialog, closeAddDialog] = useDialog()
	const [freshKey, updateFreshKey] = useState(1)
	function handleChange() {
		updateFreshKey((prev) => prev + 1)
		onChange()
	}
	return (
		<Fragment>
			<Search
				initialParams={params}
				onSearch={(newParams) => setParams({ ...newParams, page: 1 })}
				title="Room Type"
				placeholder="Search room types..."
				actions={() =>
					canManage ? (
						<Inline gap="2" alignItems="center">
							<UnlockHotelConfigInDialog
								title="Add Room"
								action="Add a new Room"
								afterAction="adding a new room"
								rateSheetStep="include the new room"
								acceptLabel="Yes, unlock & add room"
								hasChannelManager={Boolean(hotel.channel_manager)}
								onSuccess={openAddDialog}
							>
								{({ open }) => (
									<Button level="primary" onClick={open}>
										<Icons.Lock /> Add Room
									</Button>
								)}
							</UnlockHotelConfigInDialog>
							<Dropdown alignRight>
								<Dropdown.ToggleButton level="tertiary">
									<Icons.DotsVertical />
								</Dropdown.ToggleButton>
								<Dropdown.Menu>
									<Component initialState={false}>
										{({ state: open, setState }) => (
											<>
												<Dropdown.MenuItem onClick={() => setState(true)}>
													<Icons.ArrowRightLeft rotate="90" /> Rearrange
												</Dropdown.MenuItem>
												{open ? (
													<RearrangeOtaRoomTypesDialog
														hotel={hotel}
														open={open}
														onClose={() => setState(false)}
														onSuccess={() => {
															setState(false)
															handleChange()
														}}
													/>
												) : null}
											</>
										)}
									</Component>
								</Dropdown.Menu>
							</Dropdown>
						</Inline>
					) : null
				}
			>
				<ListView<TOtaRoomTypeWithMedia>
					pageKey={`hotel-${hotel.id}-ota-room-types-${freshKey}`}
					fetch={(xhr, params) =>
						hotelOtaRoomTypesXHR(xhr).getOtaRoomTypes(hotel.id, params)
					}
					params={params}
					onPageChange={(page) => setParams({ ...params, page })}
				>
					{({ items }) => (
						<OtaRoomTypesTable
							otaRoomTypes={items}
							renderActions={(roomType) =>
								canManage ? (
									<RoomTypeRowActions
										hotel={hotel}
										roomType={roomType}
										onChange={handleChange}
									/>
								) : null
							}
						/>
					)}
				</ListView>
			</Search>
			{canManage ? (
				<OtaRoomTypeInDialog
					notice={
						<HotelConfigActionRequiredAlert
							afterAction="adding this room"
							rateSheetStep="include this new room"
							hasChannelManager={Boolean(hotel.channel_manager)}
						/>
					}
					open={isAddOpen}
					onClose={closeAddDialog}
					onSubmit={async (values) => {
						await hotelOtaRoomTypesXHR(xhr).createOtaRoomType(
							hotel.id,
							otaRoomTypeFormValuesToFormData(values)
						)
						showSnackbar("Room type added successfully")
						closeAddDialog()
						handleChange()
					}}
				/>
			) : null}
		</Fragment>
	)
}

function RearrangeOtaRoomTypesDialog({
	hotel,
	open,
	onClose,
	onSuccess,
}: {
	hotel: IHotel
	open: boolean
	onClose: () => void
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const { data, error } = useSWR(
		`/api/hotels/${hotel.id}/ota-room-types?all`,
		() =>
			hotelOtaRoomTypesXHR(xhr)
				.getOtaRoomTypes(hotel.id, { limit: 1000 })
				.then((resp) => resp.data)
	)
	// null until the user moves something, so the fetched order shows until then
	const [rearranged, setRearranged] =
		useState<Array<TOtaRoomTypeWithMedia> | null>(null)
	const roomTypes = rearranged || data || []
	const dragIndexRef = useRef<number | null>(null)
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
	const [isUpdating, setIsUpdating] = useState(false)

	const hasOrderChanged = Boolean(
		rearranged?.some((roomType, index) => roomType.id !== data?.[index]?.id)
	)

	function resetDrag() {
		dragIndexRef.current = null
		setDragOverIndex(null)
	}

	function moveRoomType(from: number, to: number) {
		const next = [...roomTypes]
		const [moved] = next.splice(from, 1)
		next.splice(to, 0, moved)
		setRearranged(next)
	}

	async function handleUpdate() {
		if (!rearranged) return
		setIsUpdating(true)
		try {
			const { message } = await hotelOtaRoomTypesXHR(xhr).reorderOtaRoomTypes(
				hotel.id,
				rearranged.map((roomType, index) => ({
					id: roomType.id,
					order: index + 1,
				}))
			)
			showSnackbar(message)
			onSuccess()
		} catch (e) {
			showSnackbar(
				(e as Error).message || "Something went wrong. Please try later."
			)
		} finally {
			setIsUpdating(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose} title="Rearrange">
			<Dialog.Body>
				{error ? (
					<Text color="danger">Unable to load room types</Text>
				) : !data ? (
					<Text color="muted">Loading room types...</Text>
				) : !roomTypes.length ? (
					<Text color="muted">No room types added</Text>
				) : (
					<Stack gap="4">
						{roomTypes.map((roomType, index) => (
							<Inline key={roomType.id} gap="4" alignItems="center">
								<Box
									borderWidth="1"
									borderColor="emphasis"
									borderRadius="full"
									size="5"
									display="flex"
									alignItems="center"
									justifyContent="center"
									fontSize="xs"
									fontWeight="semibold"
								>
									{index + 1}
								</Box>
								<Box
									flex="1"
									draggable
									onDragStart={() => {
										dragIndexRef.current = index
									}}
									onDragOver={(e: React.DragEvent) => {
										e.preventDefault()
										if (
											dragIndexRef.current !== null &&
											dragIndexRef.current !== index
										) {
											setDragOverIndex(index)
										}
									}}
									onDragLeave={() => setDragOverIndex(null)}
									onDrop={(e: React.DragEvent) => {
										e.preventDefault()
										const from = dragIndexRef.current
										if (from !== null && from !== index) {
											moveRoomType(from, index)
										}
										resetDrag()
									}}
									onDragEnd={resetDrag}
									borderWidth="1"
									borderColor={
										dragOverIndex === index ? "primary_emphasis" : undefined
									}
									bgColor="default"
									rounded="lg"
									padding="3"
									style={{ cursor: "grab" }}
								>
									<Inline gap="4" alignItems="center">
										<Icons.GripVertical size="5" color="muted" />
										{roomType.thumb_image_url ? (
											<Box rounded="md" overflow="hidden" display="flex">
												<img
													src={roomType.thumb_image_url}
													alt={roomType.name}
													loading="lazy"
													style={{
														width: "48px",
														height: "48px",
														objectFit: "cover",
													}}
												/>
											</Box>
										) : null}
										<Stack gap="1">
											<Inline gap="2" alignItems="center">
												<Text fontWeight="semibold">{roomType.name}</Text>
												<CodeChip code={String(roomType.id)} />
											</Inline>
											{roomType.includes_all_meal_plans ? (
												<Text fontSize="sm" color="muted">
													All meal plans
												</Text>
											) : roomType.ota_meal_plans.length ? (
												<Inline gap="1" flexWrap="wrap">
													{roomType.ota_meal_plans.map((plan) => (
														<Box
															key={plan.id}
															paddingY="1"
															paddingX="2"
															bgColor="accent"
															color="accent"
															rounded="md"
															fontSize="xs"
														>
															{plan.name}
														</Box>
													))}
												</Inline>
											) : null}
										</Stack>
									</Inline>
								</Box>
							</Inline>
						))}
					</Stack>
				)}
			</Dialog.Body>
			<Dialog.Footer>
				<Inline justifyContent="end">
					<Button
						level="primary"
						type="button"
						disabled={!hasOrderChanged || isUpdating}
						onClick={handleUpdate}
						style={{ opacity: hasOrderChanged ? 1 : 0.5 }}
					>
						{isUpdating ? <Icons.Spinner /> : null}{" "}
						{isUpdating ? "Updating..." : "Update"}
					</Button>
				</Inline>
			</Dialog.Footer>
		</Dialog>
	)
}

// The fields the table needs, so it can show saved room types as well as the
// ones that only live in a form (e.g. hotel onboarding).
export type TOtaRoomTypesTableItem = Pick<
	TOtaRoomTypeWithMedia,
	| "name"
	| "base_adult_occupancy"
	| "base_child_occupancy"
	| "max_occupancy"
	| "max_adult_occupancy"
	| "max_child_occupancy"
	| "no_of_rooms"
	| "includes_all_meal_plans"
	| "amenities"
> &
	Partial<
		Pick<TOtaRoomTypeWithMedia, "id" | "thumb_image_url" | "ota_meal_plans">
	>

export function OtaRoomTypesTable<Item extends TOtaRoomTypesTableItem>({
	otaRoomTypes,
	renderActions,
}: {
	otaRoomTypes: Array<Item>
	renderActions: (roomType: Item, index: number) => React.ReactNode
}) {
	return (
		<Table
			bordered
			responsive
			headers={[
				"Name",
				"Base",
				"Max Occupancy",
				"No of Rooms",
				"Amenities",
				"",
			]}
			rows={otaRoomTypes.map((roomType, index) => [
				<Inline gap="4" alignItems="center">
					<Inline gap="4">
						<Thumbnail imageUrl={roomType.thumb_image_url} size="10" />
						<Stack gap="1">
							<Inline gap="2">
								<Text fontWeight="semibold">{roomType.name}</Text>
								{roomType.id ? <CodeChip code={String(roomType.id)} /> : null}
							</Inline>
							{roomType.includes_all_meal_plans ? (
								<Text fontSize="xs" color="muted">
									All meal plans
								</Text>
							) : roomType.ota_meal_plans?.length ? (
								<Inline gap="1">
									{roomType.ota_meal_plans.map((plan) => (
										<OtaMealCategoryBadge
											key={plan.id}
											category={plan.meal_category}
										/>
									))}
								</Inline>
							) : null}
						</Stack>
					</Inline>
				</Inline>,
				<Inline gap="2">
					<Inline gap="1" title="Base Adults" alignItems="center">
						<Icons.Users2 size="4" color="muted" />{" "}
						{roomType.base_adult_occupancy}
					</Inline>
					{roomType.base_child_occupancy > 0 ? (
						<Inline gap="1" title="Base Children" alignItems="center">
							<Icons.Child size="4" color="muted" />{" "}
							{roomType.base_child_occupancy}
						</Inline>
					) : null}
				</Inline>,
				<Inline gap="2">
					<Inline gap="1" title="Max Occupancy" alignItems="center">
						<Icons.UserGroup size="4" color="muted" /> {roomType.max_occupancy}
					</Inline>
					<Inline gap="1" title="Max Adults" alignItems="center">
						<Icons.Users2 size="4" color="muted" />{" "}
						{roomType.max_adult_occupancy}
					</Inline>
					{roomType.max_child_occupancy > 0 ? (
						<Inline gap="1" title="Max Children" alignItems="center">
							<Icons.Child size="4" color="muted" />{" "}
							{roomType.max_child_occupancy}
						</Inline>
					) : null}
				</Inline>,
				Number.isFinite(roomType.no_of_rooms) ? roomType.no_of_rooms : "N/A",
				roomType.amenities && roomType.amenities.length > 0 ? (
					<Component initialState={false}>
						{({ state, setState }) => (
							<>
								<Box
									onClick={() => setState(true)}
									color="primary"
									bgColor={{ hover: "subtle" }}
									rounded="md"
									paddingX="1"
									cursor="pointer"
									fontWeight="semibold"
									style={{ width: "fit-content" }}
								>
									{roomType.amenities.length}
								</Box>
								<Dialog open={state} onClose={() => setState(false)}>
									<Dialog.Header>
										<Dialog.Title>Amenities</Dialog.Title>
									</Dialog.Header>
									<Dialog.Body>
										<AmenitiesList amenities={roomType.amenities} type="room" />
									</Dialog.Body>
								</Dialog>
							</>
						)}
					</Component>
				) : (
					"N/A"
				),
				renderActions(roomType, index),
			])}
		/>
	)
}

function RoomTypeRowActions({
	hotel,
	roomType,
	onChange,
}: {
	hotel: IHotel
	roomType: TOtaRoomTypeWithMedia
	onChange: () => void
}) {
	const xhr = useXHR()
	const [isEditOpen, showEdit, hideEdit] = useDialog()
	return (
		<>
			<RowActionsMenu
				itemType="Room"
				itemName={roomType.name}
				onEdit={showEdit}
				onDelete={async () => {
					try {
						const { message } = await hotelOtaRoomTypesXHR(
							xhr
						).deleteOtaRoomType(roomType.id)
						showSnackbar(message)
						onChange()
					} catch (e) {
						showSnackbar(
							(e as Error).message || "Something went wrong. Please try later."
						)
					}
				}}
			/>
			<OtaRoomTypeInDialog
				notice={
					<HotelConfigActionRequiredAlert
						afterAction="updating this room"
						rateSheetStep="include this new room"
						hasChannelManager={Boolean(hotel.channel_manager)}
					/>
				}
				unlockCopy={{
					title: "Edit Room",
					action: "Update Room",
					afterAction: "updating this room",
					rateSheetStep: "include this new room",
					acceptLabel: "Yes, unlock room configuration",
					hasChannelManager: Boolean(hotel.channel_manager),
				}}
				roomType={otaRoomTypeToFormValues(roomType)}
				initialImageUrl={roomType.image_url}
				withMealPlanPicker
				hotelId={hotel.id}
				open={isEditOpen}
				onClose={hideEdit}
				onSubmit={async (values) => {
					await hotelOtaRoomTypesXHR(xhr).updateOtaRoomType(
						hotel.id,
						roomType.id,
						otaRoomTypeFormValuesToFormData(values)
					)
					showSnackbar("Room type updated successfully")
					hideEdit()
					onChange()
				}}
			/>
		</>
	)
}

function RowActionsMenu({
	itemType,
	itemName,
	onEdit,
	onDelete,
}: {
	itemType: string
	itemName: string
	onEdit: () => void
	onDelete: () => void | Promise<void>
}) {
	return (
		<Inline gap="2" justifyContent="end">
			<Button
				size="sm"
				level="tertiary"
				onClick={onEdit}
				title={`Edit ${itemType}`}
			>
				<Icons.Pencil />
			</Button>
			<ConfirmationDialogProvider>
				{({ confirm }) => (
					<>
						<Dropdown alignRight>
							<Dropdown.ToggleButton size="sm" level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<Dropdown.MenuItem
									color="danger"
									onClick={() => {
										confirm().then((yes) => {
											if (!yes) return
											return onDelete()
										})
									}}
								>
									<Icons.Ban /> Delete {itemType}
								</Dropdown.MenuItem>
							</Dropdown.Menu>
						</Dropdown>
						<ConfirmationDialog
							title={`Delete ${itemType}`}
							destructiveAction
							acceptActionLabel="Yes, remove"
						>
							<Alert status="error">
								Are you sure you want to Delete{" "}
								<Text as="span" fontWeight="semibold">
									{itemName}
								</Text>
								?
							</Alert>
						</ConfirmationDialog>
					</>
				)}
			</ConfirmationDialogProvider>
		</Inline>
	)
}
